import { Box, Button, Card, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { ChevronRight } from "@mui/icons-material";
import { useStyles } from "./style";
import { useTranslation } from "react-i18next";
import { RenderHTML } from "components";
import { logger } from "utils/logger";
import { ProcurementRoute, SubmitBudgetCheckModel } from "utils/graphql/Global";
import { NewLearningRequestStep } from "utils/types";
import useSubmitBudgetSignal from "../../../shared/useSubmitBudgetSignal";
import portalSettingsManager from "utils/portalSettingsManager";

interface BudgetCheckProps {
  onNext: (currentStepParam: NewLearningRequestStep) => void;
  onClose: () => void;
  isMobile?: boolean;
  setDialogTitle?: (title: string) => void;
}

export const BudgetCheck: FC<BudgetCheckProps> = ({
  onNext,
  onClose,
  isMobile,
  setDialogTitle,
}) => {
  const classes = useStyles({ isMobile });
  const { t } = useTranslation();

  // Budget check states
  const [isUnderBudget, setIsUnderBudget] = useState<boolean>(null);
  const [procurementRoute, setProcurementRoute] = useState<ProcurementRoute | null>(null);

  const submitBudget = useSubmitBudgetSignal();

  useEffect(() => {
    setDialogTitle?.(t("budget_check_title"));
  }, [setDialogTitle, t]);

  const handleClose = async () => {
    await submitBudget(
      {
        isUnderBudget: isUnderBudget,
        procurementRoute: procurementRoute,
      },
      () => {
        onClose();
        logger.info("Budget signal sent: under Budget");
      },
    );
  };

  const handleContinueToCommissioning = async () => {
    const submitBudgetCheckModel: SubmitBudgetCheckModel = {
      isUnderBudget: isUnderBudget,
      procurementRoute: ProcurementRoute.Unknown,
    };
    await submitBudget(submitBudgetCheckModel, () => {
      logger.info("Budget signal sent: Over Budget");
      onNext(NewLearningRequestStep.CommissioningDetails);
    });
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.headerSection}>
        <Typography variant="h2" className={classes.pageTitle}>
          {t("budget_check_title")}
        </Typography>
        <Typography variant="h3" className={classes.description}>
          {t("budget_check_description")}
        </Typography>
      </Box>
      <Card className={classes.card}>
        <Box className={classes.fieldsContainer}>
          <Box>
            <RenderHTML
              className={classes.fieldLabel}
              content={t("budget_check_question", {
                budget:
                  portalSettingsManager.application?.features?.learningRequestBudgetCheck
                    ?.DefaultBudgetAmount ?? "10,000",
              })}
            />
            <Box className={classes.buttonRow}>
              <Button
                id="budget-check-under-10k-btn"
                onClick={() => {
                  setIsUnderBudget(true);
                  setProcurementRoute(null);
                }}
                aria-label={t("budget_check_no_under", {
                  budget:
                    portalSettingsManager.application?.features?.learningRequestBudgetCheck
                      ?.DefaultBudgetAmount ?? "10,000",
                })}
                variant={isUnderBudget === true ? "contained" : "outlined"}
                className={isUnderBudget === true ? classes.primaryButton : classes.outlineButton}
              >
                <RenderHTML
                  content={t("budget_check_no_under", {
                    budget:
                      portalSettingsManager.application?.features?.learningRequestBudgetCheck
                        ?.DefaultBudgetAmount ?? "10,000",
                  })}
                />
              </Button>
              <Button
                id="budget-check-over-10k-btn"
                onClick={() => {
                  setIsUnderBudget(false);
                  setProcurementRoute(null);
                }}
                aria-label={t("budget_check_yes", {
                  budget:
                    portalSettingsManager.application?.features?.learningRequestBudgetCheck
                      ?.DefaultBudgetAmount ?? "10,000",
                })}
                variant={isUnderBudget === false ? "contained" : "outlined"}
                className={isUnderBudget === false ? classes.primaryButton : classes.outlineButton}
              >
                <RenderHTML
                  content={t("budget_check_yes_over", {
                    budget:
                      portalSettingsManager.application?.features?.learningRequestBudgetCheck
                        ?.DefaultBudgetAmount ?? "10,000",
                  })}
                />
              </Button>
            </Box>
          </Box>

          {isUnderBudget === true && (
            <Box>
              <Box className={classes.procurementSection}>
                <label className={classes.fieldLabel}>
                  {t("budget_check_procurement_route_question")}
                </label>
                <Box className={classes.buttonRow}>
                  <Button
                    id="budget-check-central-procurement-btn"
                    onClick={() => setProcurementRoute(ProcurementRoute.Central)}
                    aria-label={t("budget_check_central_procurement")}
                    variant={
                      procurementRoute === ProcurementRoute.Central ? "contained" : "outlined"
                    }
                    className={
                      procurementRoute === ProcurementRoute.Central
                        ? classes.primaryButton
                        : classes.outlineButton
                    }
                  >
                    {t("budget_check_central_procurement")}
                  </Button>
                  <Button
                    id="budget-check-local-procurement-btn"
                    onClick={() => setProcurementRoute(ProcurementRoute.Local)}
                    aria-label={t("budget_check_local_procurement")}
                    variant={procurementRoute === ProcurementRoute.Local ? "contained" : "outlined"}
                    className={
                      procurementRoute === ProcurementRoute.Local
                        ? classes.primaryButton
                        : classes.outlineButton
                    }
                  >
                    {t("budget_check_local_procurement")}
                  </Button>
                </Box>
              </Box>

              {procurementRoute === ProcurementRoute.Central && (
                <Box className={classes.infoBox}>
                  <RenderHTML
                    className={classes.infoText}
                    content={t("budget_check_central_info")}
                  />
                </Box>
              )}

              {procurementRoute === ProcurementRoute.Local && (
                <Box className={classes.infoBox}>
                  <RenderHTML className={classes.infoText} content={t("budget_check_local_info")} />
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box className={classes.navRow}>
          <Box className={classes.buttonRow}>
            {isUnderBudget === true && procurementRoute !== null && (
              <Button
                id="budget-check-close-btn"
                variant="contained"
                className={classes.primaryButton}
                onClick={handleClose}
                aria-label={t("close")}
              >
                {t("close")}
              </Button>
            )}

            {isUnderBudget === false && (
              <Button
                id="budget-check-continue-to-commissioning-btn"
                variant="contained"
                className={classes.primaryButton}
                onClick={handleContinueToCommissioning}
                aria-label={t("budget_check_continue_to_commissioning")}
              >
                {t("budget_check_continue_to_commissioning")}
                <ChevronRight className={classes.rightIcon} />
              </Button>
            )}
          </Box>
        </Box>
      </Card>
    </Box>
  );
};
